/**
 * AutoBuyPanel: per-asset auto-buy configuration form (Patch BB).
 *
 * Renders the controls for ONE asset's schedule (BTC or one ETF holding):
 *   - Enabled toggle (checkbox)
 *   - Amount USD per period
 *   - Cadence dropdown (Daily / Weekly / Biweekly / Monthly / Custom monthly)
 *   - Custom-monthly days input (only when cadence='custom-monthly')
 *   - Start date
 *   - Optional end date
 *
 * State storage: there's always either an existing schedule for this asset
 * in the store or none. If none, the panel renders with defaults (disabled,
 * monthly cadence, $0, today as start). The first edit calls upsertAutoBuy
 * which creates the schedule. All subsequent edits update it in place.
 *
 * The toggle from disabled -> enabled resets lastPopulatedDate to today
 * (handled in upsertAutoBuy in store.ts) so users who disable a schedule
 * for a while don't get surprise-backfilled rows when they re-enable it.
 *
 * Layout: a horizontal flex row with wrapping. On mobile the controls
 * stack vertically. The whole panel sits inside a subtle bordered box so
 * it reads as a distinct affordance from the transaction tables below.
 */

import { useState, useEffect } from 'react';
import { useAppStore } from '../state/store.js';
import { Decimal, ZERO } from '../engine/money.js';
import { DecimalInput } from './DecimalInput.js';
import type { AutoBuySchedule } from '../engine/types.js';
// Patch BD: use the local-date helper for startDate defaults so the
// picker shows the user's local calendar day, not whatever UTC date
// `new Date()` happens to be in after their local evening.
import { todayLocalNoonUtc } from '../engine/auto-buy.js';

interface AutoBuyPanelProps {
  /** 'btc' for direct BTC, 'etf' for a Bitcoin ETF holding,
   *  'security' for any non-Bitcoin security (Patch BJ). */
  assetType: 'btc' | 'etf' | 'security';
  /** 'btc' for direct BTC, holding.id for ETFs, SecurityAsset.id for securities. */
  assetId: string;
  /** Human label shown in the panel header (e.g. "BTC" or "IBIT (iShares Bitcoin Trust)"). */
  assetLabel: string;
  /** Optional: tooltip-style hint about what gets created (e.g. "no fractional shares"). */
  assetHint?: string;
}

const CADENCE_LABELS: Record<AutoBuySchedule['cadence'], string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Biweekly',
  monthly: 'Monthly',
  'custom-monthly': 'Custom monthly',
};

function parseDayList(s: string): number[] {
  // Permissive parser: split on anything non-numeric, dedupe, sort.
  // Days outside 1-31 get filtered. Empty string returns []. The walker
  // engine also clamps days > month length, so day 31 in Feb is fine.
  const parts = s.split(/[^0-9]+/).filter(p => p.length > 0);
  const seen = new Set<number>();
  for (const p of parts) {
    const n = Number.parseInt(p, 10);
    if (Number.isFinite(n) && n >= 1 && n <= 31) seen.add(n);
  }
  return [...seen].sort((a, b) => a - b);
}

function formatDayList(days: number[] | undefined): string {
  if (!days || days.length === 0) return '';
  return [...days].sort((a, b) => a - b).join(', ');
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function parseIsoDate(s: string): Date {
  // Noon UTC to dodge timezone shenanigans where midnight-UTC parsing
  // gets pushed back a calendar day in negative-offset zones.
  return new Date(s + 'T12:00:00Z');
}

export function AutoBuyPanel(props: AutoBuyPanelProps): JSX.Element {
  const { assetType, assetId, assetLabel, assetHint } = props;
  const schedule = useAppStore(s => s.autoBuySchedules.find(
    sch => sch.assetType === assetType && sch.assetId === assetId,
  ));
  const upsertAutoBuy = useAppStore(s => s.upsertAutoBuy);
  const removeAutoBuy = useAppStore(s => s.removeAutoBuy);

  // Local string state for the custom-monthly days input, since users
  // expect to type freely without each character triggering a parse.
  // We sync FROM the schedule when it changes (e.g. cloud-sync arrives)
  // and TO the schedule on blur via parseDayList.
  const [daysStr, setDaysStr] = useState<string>(() => formatDayList(schedule?.customMonthDays));
  useEffect(() => {
    setDaysStr(formatDayList(schedule?.customMonthDays));
  }, [schedule?.customMonthDays]);

  // Effective values: real schedule if present, otherwise UI defaults.
  // The "create on first edit" pattern means rendering the form with
  // defaults BEFORE the user has done anything works fine. Their first
  // change calls upsertAutoBuy which creates the row.
  const enabled = schedule?.enabled ?? false;
  const cadence = schedule?.cadence ?? 'monthly';
  const amountUsd = schedule?.amountUsd ?? ZERO;
  const startDate = schedule?.startDate ?? todayLocalNoonUtc();
  const endDate = schedule?.endDate;
  // Patch BJ: direction defaults to 'buy' for any pre-BJ schedule that
  // doesn't have the field, matching pre-BJ behavior exactly.
  const direction: 'buy' | 'sell' = schedule?.direction ?? 'buy';

  const showCustomDays = cadence === 'custom-monthly';

  return (
    <div
      style={{
        background: 'var(--accent-tint-04)',
        border: '1px solid var(--rule-strong)',
        borderRadius: 6,
        padding: 'var(--gap-m)',
        marginBottom: 'var(--gap-m)',
      }}
    >
      <div className="row between" style={{ alignItems: 'baseline', marginBottom: 'var(--gap-s)', flexWrap: 'wrap', gap: 'var(--gap-s)' }}>
        <div>
          <span className="mono" style={{ fontWeight: 600, fontSize: 'var(--fs-small)' }}>
            {/* Patch BJ: renamed from "Auto-buy" to "Auto" since the
                direction can now be sell, not just buy. */}
            Auto: {assetLabel}
          </span>
          {assetHint ? (
            <span className="faint" style={{ marginLeft: 'var(--gap-s)', fontSize: 'var(--fs-micro)' }}>
              {assetHint}
            </span>
          ) : null}
        </div>
        {/* Patch BB: remove-schedule entirely. Less destructive than deleting
            the asset; just drops the configuration. Already-created rows
            stay in the user's history (per Paul's "dont disable old auto buy
            rows" requirement). */}
        {schedule ? (
          <button
            className="ghost"
            onClick={() => {
              if (confirm('Remove this auto-buy schedule? Already-created rows in your transactions will stay; only future scheduled buys stop.')) {
                removeAutoBuy(assetType, assetId);
              }
            }}
            style={{ padding: '2px 8px', fontSize: 'var(--fs-micro)', color: 'var(--text-soft)' }}
            title="Drop this auto-buy schedule. Existing rows stay; future buys stop."
          >
            remove
          </button>
        ) : null}
      </div>

      <div className="row" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 'var(--gap-m)' }}>
        {/* Enabled toggle. Visually a checkbox + label so it's obvious
            what's on/off. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-small)' }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={e => upsertAutoBuy(assetType, assetId, { enabled: e.target.checked })}
          />
          <span>Enabled</span>
        </label>

        {/* Amount USD per period. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-small)' }}>
          <span className="faint">$</span>
          <DecimalInput
            step="10"
            decimals={2}
            placeholder="100"
            value={amountUsd}
            onChange={d => upsertAutoBuy(assetType, assetId, { amountUsd: d })}
            style={{ width: 90 }}
          />
          <span className="faint" style={{ fontSize: 'var(--fs-micro)' }}>per period</span>
        </div>

        {/* Patch BJ: Buy / Sell dropdown. Default 'buy' matches pre-BJ
            behavior for every existing schedule. 'sell' is mainly useful
            for the new Stocks & ETFs / Metals sub-cards (e.g. "sell $1k
            of SPY monthly to fund retirement"); also valid on BTC and
            ETF schedules though most users won't reach for it. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-small)' }}>
          <select
            value={direction}
            onChange={e => upsertAutoBuy(assetType, assetId, { direction: e.target.value as 'buy' | 'sell' })}
            style={{ fontSize: 'var(--fs-small)' }}
            title="Whether each scheduled period adds (buy) or removes (sell) from the position."
          >
            <option value="buy">Buy</option>
            <option value="sell">Sell</option>
          </select>
        </div>

        {/* Cadence. Patch BC: dropped the "every" label that used to sit
            here. "every Monthly" and "every Daily" both read awkwardly;
            the cadence noun on its own (Daily / Weekly / Monthly / ...)
            carries the recurrence meaning fine, and the "per period"
            tag on the amount input still anchors what the dropdown
            applies to. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-small)' }}>
          <select
            value={cadence}
            onChange={e => upsertAutoBuy(assetType, assetId, { cadence: e.target.value as AutoBuySchedule['cadence'] })}
            style={{ minWidth: 90 }}
          >
            {Object.entries(CADENCE_LABELS).map(([v, label]) => (
              <option key={v} value={v}>{label}</option>
            ))}
          </select>
        </div>

        {/* Custom-monthly days input. Only visible when cadence requires it. */}
        {showCustomDays ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-small)' }}>
            <span className="faint">days:</span>
            <input
              type="text"
              placeholder="1, 15"
              value={daysStr}
              onChange={e => setDaysStr(e.target.value)}
              onBlur={() => {
                const parsed = parseDayList(daysStr);
                upsertAutoBuy(assetType, assetId, { customMonthDays: parsed });
                // Re-format the input to canonical "1, 15" form.
                setDaysStr(formatDayList(parsed));
              }}
              style={{ width: 90, fontFamily: 'var(--mono)' }}
              title="Comma-separated days of the month, e.g. '1, 15' for the 1st and 15th. Out-of-month days (e.g. 31 in Feb) clamp to the last day of that month."
            />
          </div>
        ) : null}

        {/* Start date. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-small)' }}>
          <span className="faint">start</span>
          <input
            type="date"
            value={isoDate(startDate)}
            onChange={e => upsertAutoBuy(assetType, assetId, { startDate: parseIsoDate(e.target.value) })}
            style={{ width: 130 }}
          />
        </div>

        {/* End date (optional). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-small)' }}>
          <span className="faint">end</span>
          <input
            type="date"
            value={endDate ? isoDate(endDate) : ''}
            onChange={e => {
              if (e.target.value) {
                upsertAutoBuy(assetType, assetId, { endDate: parseIsoDate(e.target.value) });
              } else {
                upsertAutoBuy(assetType, assetId, { endDate: undefined });
              }
            }}
            style={{ width: 130 }}
            title="Optional. Leave blank to run indefinitely."
          />
        </div>
      </div>

      {/* Status line: shows what the walker will do. Always visible (even
          when disabled) so the user can see the consequence of their
          settings before flipping the toggle. */}
      <div className="faint mono" style={{ marginTop: 'var(--gap-s)', fontSize: 'var(--fs-micro)', lineHeight: 1.5 }}>
        {!enabled ? (
          <>Disabled. Toggle Enabled to start.</>
        ) : amountUsd.lessThanOrEqualTo(0) ? (
          <>Enabled, but amount is $0. No rows will be created.</>
        ) : cadence === 'custom-monthly' && (!schedule?.customMonthDays || schedule.customMonthDays.length === 0) ? (
          <>Enabled, but no custom days set. No rows will be created.</>
        ) : (
          <>
            Will create a {assetType === 'btc' ? 'BTC purchase' : 'share purchase'}{' '}
            on each {CADENCE_LABELS[cadence].toLowerCase()} date at today&apos;s price.
            {assetType === 'etf' ? <> No fractional shares: leftover dollars discard.</> : null}
            {' '}Rows are tagged AUTO; any edit removes the tag.
          </>
        )}
      </div>
    </div>
  );
}
